import React, { useEffect, useMemo, useState } from 'react';
import {
  ArrowPathIcon,
  CheckCircleIcon,
  DocumentMagnifyingGlassIcon,
  ExclamationTriangleIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/outline';
import { ComparisonResult, DocumentVersion } from '../types/documentVersion.types';
import { Button } from '@/components/ui/button';
import { useDocumentAPI } from '../../../DocumentAPI';

interface ComparisonViewProps {
  comparisonResult: ComparisonResult | null;
  showComparison: boolean;
  selectedVersions: string[];
  documentId?: string;
  versions: DocumentVersion[];
}

const COMPARE_PROGRESS_STAGES = [
  { label: 'Resolving DMS versions', detail: 'Loading the two selected version records.' },
  { label: 'Extracting DOCX text', detail: 'Reading Word OOXML text from each file.' },
  { label: 'Classifying diff categories', detail: 'Separating formatting, wording, and legal-context changes.' },
  { label: 'Preparing attorney summary', detail: 'Building the review narrative and audit package.' },
] as const;

export const ComparisonView: React.FC<ComparisonViewProps> = ({
  showComparison,
  selectedVersions,
  documentId,
  versions,
}) => {
  const documentAPI = useDocumentAPI();
  const [report, setReport] = useState<any>(null);
  const [isRunning, setIsRunning] = useState(false);
  const [compareProgress, setCompareProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'summary' | 'redline' | 'visual' | 'audit'>('summary');
  const [changeFilter, setChangeFilter] = useState<'all' | 'legal' | 'formatting' | 'wording'>('all');
  const [previewUrls, setPreviewUrls] = useState<{ base?: string; comparison?: string }>({});

  const selectedVersionObjects = useMemo(() => {
    return selectedVersions
      .map((id) => versions.find((version) => version.version_id === id))
      .filter(Boolean) as DocumentVersion[];
  }, [selectedVersions, versions]);

  const orderedVersions = useMemo(() => {
    return [...selectedVersionObjects].sort((a, b) => {
      const aTime = new Date(a.uploaded_at || a.created_at || 0).getTime();
      const bTime = new Date(b.uploaded_at || b.created_at || 0).getTime();
      return aTime - bTime;
    });
  }, [selectedVersionObjects]);

  const canCompare = !!documentId && orderedVersions.length === 2;
  const reportChanges = useMemo(() => (Array.isArray(report?.changes) ? report.changes : []), [report]);
  const changeCounts = useMemo(() => {
    return reportChanges.reduce((counts: Record<string, number>, change: any) => {
      const category = change.changeCategory || 'wording';
      counts.all += 1;
      if (category === 'legal-context' || change.legalRelevance === 'medium' || change.legalRelevance === 'high') counts.legal += 1;
      if (category === 'formatting' || category === 'punctuation') counts.formatting += 1;
      if (category === 'wording') counts.wording += 1;
      return counts;
    }, { all: 0, legal: 0, formatting: 0, wording: 0 });
  }, [reportChanges]);
  const visibleChanges = useMemo(() => {
    if (changeFilter === 'all') return reportChanges;
    if (changeFilter === 'legal') {
      return reportChanges.filter((change: any) => (
        change.changeCategory === 'legal-context' ||
        change.legalRelevance === 'medium' ||
        change.legalRelevance === 'high'
      ));
    }
    if (changeFilter === 'formatting') {
      return reportChanges.filter((change: any) => change.changeCategory === 'formatting' || change.changeCategory === 'punctuation');
    }
    return reportChanges.filter((change: any) => change.changeCategory === 'wording');
  }, [changeFilter, reportChanges]);
  const visibleRedlineHtml = useMemo(() => buildRedlineHtml(visibleChanges), [visibleChanges]);
  const activeProgressStage = Math.min(
    COMPARE_PROGRESS_STAGES.length - 1,
    Math.floor((compareProgress / 100) * COMPARE_PROGRESS_STAGES.length),
  );

  useEffect(() => {
    setReport(null);
    setError(null);
    setPreviewUrls({});
  }, [selectedVersions.join('|')]);

  useEffect(() => {
    if (!isRunning) return;

    const timer = window.setInterval(() => {
      setCompareProgress((current) => {
        if (current < 22) return current + 4;
        if (current < 58) return current + 3;
        if (current < 86) return current + 2;
        return Math.min(current + 1, 94);
      });
    }, 450);

    return () => window.clearInterval(timer);
  }, [isRunning]);

  const runCompare = React.useCallback(async () => {
    if (!documentId || orderedVersions.length !== 2) return;
    setIsRunning(true);
    setCompareProgress(6);
    setError(null);
    setReport(null);
    setPreviewUrls({});
    try {
      const response = await documentAPI.compareVersions(
        documentId,
        orderedVersions[0].version_id,
        orderedVersions[1].version_id,
        { includeAi: true },
      );
      setCompareProgress(100);
      setReport(response?.comparison || response);
      setActiveTab('summary');
    } catch (compareError) {
      const message = compareError instanceof Error ? compareError.message : 'Version compare failed.';
      setError(message);
    } finally {
      setIsRunning(false);
    }
  }, [documentAPI, documentId, orderedVersions]);

  useEffect(() => {
    if (showComparison && canCompare && !report && !isRunning && !error) {
      runCompare();
    }
  }, [canCompare, error, isRunning, report, runCompare, showComparison]);

  useEffect(() => {
    if (!report?.preview) return;
    let alive = true;
    const objectUrls: string[] = [];

    const loadPreview = async (key: 'base' | 'comparison', url: string) => {
      const response = await fetch(url, { headers: documentAPI.getAuthHeaders() });
      if (!response.ok) return;
      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      objectUrls.push(objectUrl);
      if (alive) setPreviewUrls((previous) => ({ ...previous, [key]: objectUrl }));
    };

    loadPreview('base', report.preview.baseUrl).catch(() => {});
    loadPreview('comparison', report.preview.comparisonUrl).catch(() => {});

    return () => {
      alive = false;
      objectUrls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [documentAPI, report]);

  return (
    <div className="space-y-5">
      <div className="rounded-2xl border border-[var(--brand-border)] bg-[var(--brand-accent-primary)] p-5 text-[var(--brand-accent-contrast)] shadow-sm">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.22em] text-[var(--brand-accent-soft)]">
              <DocumentMagnifyingGlassIcon className="h-4 w-4" />
              EdgeLex Compare Studio
            </div>
            <h4 className="mt-2 text-xl font-semibold">MS Word version comparison</h4>
            <p className="mt-1 max-w-2xl text-sm text-[color-mix(in_oklab,var(--brand-accent-contrast)_82%,transparent)]">
              Compare two DMS versions using DOCX text extraction, native Word revision signals, EdgeLex visual previews, and attorney-focused risk triage.
            </p>
          </div>
          <Button
            onClick={runCompare}
            disabled={!canCompare || isRunning}
            className="bg-[var(--brand-accent-contrast)] text-[var(--brand-accent-primary)] hover:bg-[color-mix(in_oklab,var(--brand-accent-contrast)_88%,transparent)] disabled:opacity-50"
          >
            {isRunning ? (
              <span className="inline-flex items-center gap-2">
                <ArrowPathIcon className="h-4 w-4 animate-spin" />
                {Math.round(compareProgress)}%
              </span>
            ) : 'Run Word Compare'}
          </Button>
        </div>

        <div className="mt-5 grid gap-3 md:grid-cols-2">
          {orderedVersions.map((version, index) => (
            <div key={version.version_id} className="rounded-xl border border-[color-mix(in_oklab,var(--brand-accent-contrast)_18%,transparent)] bg-[color-mix(in_oklab,var(--brand-accent-contrast)_10%,transparent)] p-3">
              <div className="text-xs uppercase tracking-wide text-[var(--brand-accent-soft)]">{index === 0 ? 'Base version' : 'Comparison version'}</div>
              <div className="mt-1 font-semibold">{version.version_number}</div>
              <div className="truncate text-sm text-[color-mix(in_oklab,var(--brand-accent-contrast)_84%,transparent)]">{version.original_name}</div>
              <div className="mt-1 text-xs text-[color-mix(in_oklab,var(--brand-accent-contrast)_68%,transparent)]">{formatDate(version.uploaded_at || version.created_at)}</div>
            </div>
          ))}
          {orderedVersions.length < 2 && (
            <div className="rounded-xl border border-[color-mix(in_oklab,var(--brand-accent-contrast)_18%,transparent)] bg-[color-mix(in_oklab,var(--brand-accent-contrast)_10%,transparent)] p-3 text-sm text-[color-mix(in_oklab,var(--brand-accent-contrast)_84%,transparent)] md:col-span-2">
              Select exactly two versions from Version History, then click Compare. The older selected version becomes the base.
            </div>
          )}
        </div>
      </div>

      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700 dark:border-red-900/50 dark:bg-red-950/30 dark:text-red-200">
          <div className="font-semibold">Compare could not run</div>
          <div className="mt-1">{error}</div>
        </div>
      )}

      {isRunning && (
        <div className="rounded-xl border border-[var(--brand-border)] bg-[var(--brand-accent-primary-soft)] p-4 text-sm text-[var(--brand-text-primary)] shadow-sm">
          <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
            <div>
              <div className="font-semibold">Building comparison report</div>
              <div className="mt-1 text-xs text-[var(--brand-text-secondary)]">
                {COMPARE_PROGRESS_STAGES[activeProgressStage]?.detail}
              </div>
            </div>
            <div className="font-mono text-xs font-semibold">{Math.round(compareProgress)}%</div>
          </div>
          <div className="mt-4 h-2 overflow-hidden rounded-full bg-[var(--brand-surface-card)]">
            <div
              className="h-full rounded-full bg-[var(--brand-accent-primary)] transition-all duration-500 ease-out"
              style={{ width: `${compareProgress}%` }}
            />
          </div>
          <div className="mt-4 grid gap-2 md:grid-cols-4">
            {COMPARE_PROGRESS_STAGES.map((stage, index) => {
              const isComplete = index < activeProgressStage;
              const isActive = index === activeProgressStage;
              return (
                <div
                  key={stage.label}
                  className={`rounded-lg border px-3 py-2 transition-colors ${
                    isComplete
                      ? 'border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-800/60 dark:bg-emerald-950/30 dark:text-emerald-200'
                      : isActive
                        ? 'border-[var(--brand-accent-primary)] bg-[var(--brand-surface-card)] text-[var(--brand-text-primary)] shadow-sm'
                        : 'border-transparent bg-[var(--brand-surface-card)] text-[var(--brand-text-secondary)] opacity-75'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    {isComplete ? (
                      <CheckCircleIcon className="h-4 w-4" />
                    ) : isActive ? (
                      <ArrowPathIcon className="h-4 w-4 animate-spin" />
                    ) : (
                      <span className="h-4 w-4 rounded-full border border-current opacity-40" />
                    )}
                    <span className="font-medium">{stage.label}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {report && (
        <div className="space-y-4">
              <div className="grid gap-3 md:grid-cols-4">
                <MetricCard label="Changes" value={report.summary?.totalChanges ?? 0} />
                <MetricCard label="Legal Context" value={report.summary?.legalContextChanges ?? changeCounts.legal} tone="amber" />
                <MetricCard label="Formatting" value={(report.summary?.formattingChanges ?? 0) + (report.summary?.punctuationChanges ?? 0) || changeCounts.formatting} tone="blue" />
                <MetricCard label="Wording" value={report.summary?.wordingChanges ?? changeCounts.wording} tone="green" />
              </div>

          <ReviewBrief report={report} changeCounts={changeCounts} />

              <div className="flex flex-wrap gap-2">
            {(['summary', 'redline', 'visual', 'audit'] as const).map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                  activeTab === tab
                    ? 'bg-lex-accent text-lex-accent-contrast'
                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700'
                }`}
              >
                {tab === 'summary' ? 'Change Navigator' : tab === 'redline' ? 'Redline' : tab === 'visual' ? 'Visual Compare' : 'Audit'}
              </button>
            ))}
              </div>

              <div className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm dark:border-slate-700 dark:bg-slate-900">
                <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                  Diff Lens
                </div>
                <div className="flex flex-wrap gap-2">
                  {([
                    ['all', 'All Changes', changeCounts.all],
                    ['legal', 'Legal Context', changeCounts.legal],
                    ['formatting', 'Formatting / Punctuation', changeCounts.formatting],
                    ['wording', 'Wording', changeCounts.wording],
                  ] as const).map(([filter, label, count]) => (
                    <button
                      key={filter}
                      type="button"
                      onClick={() => setChangeFilter(filter)}
                      className={`rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${
                        changeFilter === filter
                          ? 'bg-slate-950 text-white dark:bg-white dark:text-slate-950'
                          : 'bg-slate-100 text-slate-700 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700'
                      }`}
                    >
                      {label} <span className="ml-1 opacity-70">{count}</span>
                    </button>
                  ))}
                </div>
                <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
                  EdgeLex keeps every diff in the audit trail, but separates cleanup changes from legally relevant changed tokens.
                </p>
              </div>

              {activeTab === 'summary' && (
                <div className="space-y-3">
                  {visibleChanges.slice(0, 80).map((change: any) => (
                    <div key={change.id} className="rounded-xl border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-900">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${changePillClass(change.type)}`}>
                          {change.label}
                        </span>
                        <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${categoryPillClass(change.changeCategory)}`}>
                          {formatChangeCategory(change.changeCategory)}
                        </span>
                        <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${relevancePillClass(change.legalRelevance)}`}>
                          {formatLegalRelevance(change.legalRelevance)}
                        </span>
                        {(change.riskFlags || []).map((flag: string) => (
                          <span key={flag} className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800 dark:bg-amber-900/40 dark:text-amber-200">
                            <ExclamationTriangleIcon className="h-3 w-3" />
                            {flag}
                          </span>
                    ))}
                  </div>
                      <div className="mt-3 grid gap-3 md:grid-cols-2">
                    {change.baseText && (
                      <div className="rounded-lg bg-red-50 p-3 text-sm text-red-900 dark:bg-red-950/30 dark:text-red-100">
                        <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-red-500">Before</div>
                        <p className="leading-6">{renderHighlightedText(change.baseText, getHighlightTerms(change, 'base'), 'removed')}</p>
                      </div>
                    )}
                    {change.comparisonText && (
                      <div className="rounded-lg bg-emerald-50 p-3 text-sm text-emerald-900 dark:bg-emerald-950/30 dark:text-emerald-100">
                        <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-emerald-600">After</div>
                        <p className="leading-6">{renderHighlightedText(change.comparisonText, getHighlightTerms(change, 'comparison'), 'added')}</p>
                      </div>
                    )}
                      </div>
                      {(change.changedText || change.legalRationale) && (
                        <div className="mt-3 rounded-lg bg-slate-50 p-3 text-xs text-slate-600 dark:bg-slate-950/50 dark:text-slate-300">
                          {change.changedText && (
                            <div><span className="font-semibold">Changed tokens:</span> {change.changedText}</div>
                          )}
                          {change.legalRationale && (
                            <div className="mt-1"><span className="font-semibold">Classification:</span> {change.legalRationale}</div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                  {visibleChanges.length === 0 && (
                    <div className="rounded-xl border border-slate-200 bg-white p-6 text-center text-sm text-slate-500 dark:border-slate-700 dark:bg-slate-900">
                      No changes match this diff lens.
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'redline' && (
                <div
                  className="max-h-[620px] overflow-y-auto rounded-xl border border-slate-200 bg-white p-5 text-sm leading-7 text-slate-900 shadow-sm dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 [&_del]:rounded [&_del]:bg-red-100 [&_del]:px-1 [&_del]:text-red-800 [&_ins]:rounded [&_ins]:bg-emerald-100 [&_ins]:px-1 [&_ins]:text-emerald-800"
                  dangerouslySetInnerHTML={{ __html: visibleRedlineHtml || '' }}
                />
              )}

          {activeTab === 'visual' && (
            <div className="grid gap-4 xl:grid-cols-2">
              <PreviewFrame title={`${report.versions?.base?.versionNumber} visual preview`} url={previewUrls.base} />
              <PreviewFrame title={`${report.versions?.comparison?.versionNumber} visual preview`} url={previewUrls.comparison} />
            </div>
          )}

          {activeTab === 'audit' && (
            <div className="space-y-3 rounded-xl border border-slate-200 bg-white p-4 text-sm dark:border-slate-700 dark:bg-slate-900">
              <AuditRow label="Report ID" value={report.reportId} />
              <AuditRow label="Base file hash" value={report.audit?.baseFileHash} />
              <AuditRow label="Comparison file hash" value={report.audit?.comparisonFileHash} />
              <AuditRow label="Report hash" value={report.audit?.reportHash} />
              <AuditRow label="Extractor" value={report.audit?.extractor} />
              <AuditRow label="Renderer" value={report.audit?.renderer} />
              <div>
                <div className="mb-1 font-medium text-slate-900 dark:text-white">Native Word revisions</div>
                <div className="max-h-64 overflow-y-auto rounded-lg bg-slate-50 p-3 dark:bg-slate-950/50">
                  {(report.trackedRevisions || []).length ? (
                    (report.trackedRevisions || []).slice(0, 80).map((revision: any, index: number) => (
                      <div key={`${revision.sourcePart}-${index}`} className="border-b border-slate-200 py-2 last:border-0 dark:border-slate-700">
                        <div className="font-medium">{revision.description}</div>
                        <div className="text-xs text-slate-500">{[revision.author, revision.date, revision.sourcePart].filter(Boolean).join(' | ')}</div>
                      </div>
                    ))
                  ) : (
                    <div className="text-slate-500">No native Word revision records were found in the comparison version.</div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const MetricCard = ({ label, value, tone = 'blue' }: { label: string; value: number | string; tone?: 'blue' | 'green' | 'red' | 'amber' }) => {
  const toneClass = {
    blue: 'text-blue-600 bg-blue-50 dark:bg-blue-950/30 dark:text-blue-200',
    green: 'text-emerald-700 bg-emerald-50 dark:bg-emerald-950/30 dark:text-emerald-200',
    red: 'text-red-700 bg-red-50 dark:bg-red-950/30 dark:text-red-200',
    amber: 'text-amber-700 bg-amber-50 dark:bg-amber-950/30 dark:text-amber-200',
  }[tone];

  return (
    <div className={`rounded-xl border border-slate-200 p-4 dark:border-slate-700 ${toneClass}`}>
      <div className="text-xs font-semibold uppercase tracking-wide opacity-70">{label}</div>
      <div className="mt-1 text-2xl font-semibold">{value}</div>
    </div>
  );
};

const ReviewBrief = ({ report, changeCounts }: { report: any; changeCounts: Record<string, number> }) => {
  const summary = report.summary || {};
  const bullets = Array.isArray(report.attorneySummary?.bullets) ? report.attorneySummary.bullets : [];
  const aiItems = parseAiSummary(report.attorneySummary?.aiText);
  const legalFlags = Array.isArray(report.attorneySummary?.riskFlags) ? report.attorneySummary.riskFlags : [];

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-700 dark:bg-slate-900">
      <div className="flex items-start gap-3">
        <ShieldCheckIcon className="mt-0.5 h-5 w-5 text-blue-500" />
        <div className="min-w-0 flex-1">
          <div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
            <div>
              <h5 className="font-semibold text-slate-900 dark:text-white">
                {report.attorneySummary?.headline || 'Comparison review brief'}
              </h5>
              <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                Built from the deterministic diff first; AI narrative is supplemental.
              </p>
            </div>
            <div className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-700 dark:bg-slate-800 dark:text-slate-200">
              {changeCounts.all} total changes
            </div>
          </div>

          <div className="mt-4 grid gap-3 lg:grid-cols-3">
            <BriefTile
              title="What Changed"
              tone="blue"
              lines={[
                `${summary.additions ?? 0} additions, ${summary.deletions ?? 0} deletions, ${summary.modifications ?? 0} modifications`,
                `Word count ${formatDelta(summary.wordDelta ?? 0)} words`,
              ]}
            />
            <BriefTile
              title="Needs Legal Review"
              tone="amber"
              lines={[
                `${summary.legalContextChanges ?? changeCounts.legal} legally relevant change${(summary.legalContextChanges ?? changeCounts.legal) === 1 ? '' : 's'}`,
                legalFlags.length ? legalFlags.join(', ') : 'No legal-context flags detected',
              ]}
            />
            <BriefTile
              title="Cleanup / Noise"
              tone="slate"
              lines={[
                `${(summary.formattingChanges ?? 0) + (summary.punctuationChanges ?? 0) || changeCounts.formatting} formatting or punctuation changes`,
                'Tracked separately so cleanup does not masquerade as legal risk',
              ]}
            />
          </div>

          {bullets.length > 0 && (
            <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 p-3 dark:border-slate-700 dark:bg-slate-950/40">
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">Deterministic Findings</div>
              <div className="mt-2 grid gap-2 md:grid-cols-2">
                {bullets.map((bullet: string, index: number) => (
                  <div key={`${bullet}-${index}`} className="rounded-lg bg-white px-3 py-2 text-sm text-slate-700 shadow-sm dark:bg-slate-900 dark:text-slate-300">
                    {bullet}
                  </div>
                ))}
              </div>
            </div>
          )}

          {aiItems.length > 0 && (
            <details className="mt-4 rounded-xl border border-blue-200 bg-blue-50 p-3 text-blue-900 dark:border-blue-900/50 dark:bg-blue-950/30 dark:text-blue-100">
              <summary className="cursor-pointer text-sm font-semibold">AI narrative explanation</summary>
              <div className="mt-3 space-y-2 text-sm">
                {aiItems.map((item, index) => (
                  <div key={`${item}-${index}`} className="rounded-lg bg-white/70 px-3 py-2 dark:bg-white/10">
                    {item}
                  </div>
                ))}
              </div>
            </details>
          )}
        </div>
      </div>
    </div>
  );
};

const BriefTile = ({
  title,
  lines,
  tone,
}: {
  title: string;
  lines: string[];
  tone: 'blue' | 'amber' | 'slate';
}) => {
  const toneClass = {
    blue: 'border-blue-200 bg-blue-50 text-blue-900 dark:border-blue-900/50 dark:bg-blue-950/30 dark:text-blue-100',
    amber: 'border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-900/50 dark:bg-amber-950/30 dark:text-amber-100',
    slate: 'border-slate-200 bg-slate-50 text-slate-800 dark:border-slate-700 dark:bg-slate-950/40 dark:text-slate-200',
  }[tone];

  return (
    <div className={`rounded-xl border p-3 ${toneClass}`}>
      <div className="text-xs font-semibold uppercase tracking-wide opacity-70">{title}</div>
      <div className="mt-2 space-y-1 text-sm">
        {lines.map((line) => (
          <div key={line}>{line}</div>
        ))}
      </div>
    </div>
  );
};

const PreviewFrame = ({ title, url }: { title: string; url?: string }) => (
  <div className="rounded-xl border border-slate-200 bg-white p-3 dark:border-slate-700 dark:bg-slate-900">
    <div className="mb-3 font-medium text-slate-900 dark:text-white">{title}</div>
    {url ? (
      <iframe title={title} src={url} className="h-[620px] w-full rounded-lg border border-slate-200 bg-white dark:border-slate-700" />
    ) : (
      <div className="flex h-[620px] items-center justify-center rounded-lg border border-dashed border-slate-300 text-sm text-slate-500 dark:border-slate-700">
        Preparing authenticated EdgeLex visual preview...
      </div>
    )}
  </div>
);

const AuditRow = ({ label, value }: { label: string; value?: string }) => (
  <div className="grid gap-2 border-b border-slate-100 pb-2 last:border-0 md:grid-cols-[180px_1fr] dark:border-slate-800">
    <div className="font-medium text-slate-700 dark:text-slate-300">{label}</div>
    <div className="break-all font-mono text-xs text-slate-600 dark:text-slate-400">{value || 'Not recorded'}</div>
  </div>
);

function changePillClass(type: string): string {
  if (type === 'added') return 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200';
  if (type === 'removed') return 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200';
  return 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200';
}

function categoryPillClass(category?: string): string {
  if (category === 'legal-context') return 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200';
  if (category === 'formatting' || category === 'punctuation') return 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200';
  return 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-200';
}

function relevancePillClass(relevance?: string): string {
  if (relevance === 'high') return 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200';
  if (relevance === 'medium') return 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200';
  if (relevance === 'low') return 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200';
  return 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300';
}

function formatChangeCategory(category?: string): string {
  if (category === 'legal-context') return 'Legal context';
  if (category === 'formatting') return 'Formatting';
  if (category === 'punctuation') return 'Punctuation';
  return 'Wording';
}

function formatLegalRelevance(relevance?: string): string {
  if (relevance === 'high') return 'High legal relevance';
  if (relevance === 'medium') return 'Medium legal relevance';
  if (relevance === 'low') return 'Low legal relevance';
  return 'No legal relevance';
}

function buildRedlineHtml(changes: any[]): string {
  if (!changes.length) {
    return '<p class="edgelex-compare-empty">No changes match this diff lens.</p>';
  }

  return changes.slice(0, 250).map((change) => {
    const category = escapeHtml(formatChangeCategory(change.changeCategory));
    const relevance = escapeHtml(formatLegalRelevance(change.legalRelevance));
    const heading = `<div class="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">${category} | ${relevance}</div>`;
    if (change.type === 'added') {
      return `<section class="mb-4">${heading}<p class="edgelex-compare-added"><ins>${escapeHtml(change.comparisonText || '')}</ins></p></section>`;
    }
    if (change.type === 'removed') {
      return `<section class="mb-4">${heading}<p class="edgelex-compare-removed"><del>${escapeHtml(change.baseText || '')}</del></p></section>`;
    }
    return `<section class="mb-4">${heading}<p class="edgelex-compare-modified"><del>${escapeHtml(change.baseText || '')}</del><br/><ins>${escapeHtml(change.comparisonText || '')}</ins></p></section>`;
  }).join('\n');
}

function getHighlightTerms(change: any, side: 'base' | 'comparison'): string[] {
  const changedText = String(change?.changedText || '');
  const directional = changedText
    .split(/\s+/)
    .filter(Boolean)
    .filter((token) => side === 'base' ? token.startsWith('-') : token.startsWith('+'))
    .map((token) => token.replace(/^[-+]/, ''));

  if (directional.length > 0) {
    return directional;
  }

  if (change?.type === 'added' && side === 'comparison') {
    return tokenizeForHighlight(change.comparisonText).slice(0, 40);
  }
  if (change?.type === 'removed' && side === 'base') {
    return tokenizeForHighlight(change.baseText).slice(0, 40);
  }
  return [];
}

function renderHighlightedText(text: string, terms: string[], tone: 'added' | 'removed'): React.ReactNode {
  if (!terms.length) return text;
  const normalizedTerms = new Set(terms.map(normalizeHighlightToken).filter((term) => term.length > 1));
  if (!normalizedTerms.size) return text;

  const highlightClass = tone === 'added'
    ? 'rounded bg-emerald-200/80 px-0.5 font-semibold text-emerald-950 ring-1 ring-emerald-300 dark:bg-emerald-300/30 dark:text-emerald-50 dark:ring-emerald-500/40'
    : 'rounded bg-red-200/80 px-0.5 font-semibold text-red-950 ring-1 ring-red-300 dark:bg-red-300/30 dark:text-red-50 dark:ring-red-500/40';

  return text.split(/(\b[\w’']+\b)/g).map((part, index) => {
    const normalized = normalizeHighlightToken(part);
    if (!normalizedTerms.has(normalized)) return part;
    return (
      <mark key={`${part}-${index}`} className={highlightClass}>
        {part}
      </mark>
    );
  });
}

function tokenizeForHighlight(value?: string): string[] {
  return String(value || '').match(/\b[\w’']+\b/g) || [];
}

function normalizeHighlightToken(value: string): string {
  return String(value || '')
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[’‘]/g, "'")
    .replace(/[^a-z0-9]/g, '');
}

function parseAiSummary(value?: string): string[] {
  return String(value || '')
    .replace(/\*\*/g, '')
    .split(/\n|(?:^|\s)-\s+/)
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => line.replace(/^#+\s*/, '').replace(/^[-•]\s*/, '').trim())
    .filter((line) => line.length > 0)
    .slice(0, 12);
}

function formatDelta(value: number): string {
  return value >= 0 ? `+${value}` : `${value}`;
}

function escapeHtml(value: string): string {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

function formatDate(value?: string): string {
  if (!value) return 'No date recorded';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
}
